import { Fragment, useContext, useEffect, useState } from "react";
import { Context } from "./context";
import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import * as _ from "lodash";

import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import * as FCU from "@fluentui/react-components/unstable";
import * as FCB from "@fluentui/react-breadcrumb-preview";

import { Version } from "./pages/system/version";
import { FieldDetail, FieldManagement } from "./pages/field/field";
import { getCurrentUser, logoutUser } from "./services/login";
import { TurbineManagement } from "./pages/field/turbine";
import { TaskDetail, TaskManagement, TaskNew } from "./pages/task/management";
import { TaskCheck } from "./pages/task/check";
import { TaskAnnotation } from "./pages/task/annotation";
import GroundboxGallery from "./pages/groundbox-gallery";
import { AccountIndex } from "./pages/system/account_index";
import { TenantScopedEntryRedirect } from "./components/tenant_scoped_entry_redirect";
import { DeviceManagement } from "./pages/system/device";
import { UsageReport } from "./pages/system/usage_report";
import { TenantManagementWorkbench } from "./pages/system/tenant_workbench";
import { UpdateSigningKeyManagement } from "./pages/system/update_signing_keys";
import {
    AssetOwnerManagement,
    TenantManagement,
} from "./pages/system/tenant_management";
import { EquipmentManagement } from "./pages/equipment/equipment_management";
import { EquipmentDetail } from "./pages/equipment/equipment_detail";
import { ServiceRecordList } from "./pages/equipment/service_record_list";
import { EquipmentRouteGuard } from "./components/equipment/equipment_route_guard";
import { LeafManagement } from "./pages/field/leaf";
import { BusinessManagement } from "./pages/field/business";
import { DatacenterFieldManagement } from "./pages/datacenter/field_simple";
import { DatacenterFieldManagementTest } from "./pages/datacenter/field_test";
import { TaskRecheck, TaskRecheckDetail } from "./pages/task/recheck";
import { RawDataManagement } from "./pages/system/raw_data";
import { DatacenterTaskManagement } from "./pages/datacenter/task";
import { DatacenterViewFrames } from "./pages/datacenter/view_frames";
import {
    DatacenterViewTask,
    DatacenterViewTaskManagement,
} from "./pages/datacenter/view_task";
import { DatacenterDefectionManagement } from "./pages/datacenter/defection";
import { TaskReport } from "./pages/task/report";
import { Log } from "./pages/system/log";
import { SingleReportPage } from "./pages/task/single_report";
import {
    FieldItemDef,
    field_item_searchable_columns,
} from "./services/field_item_service";
import { filterToCondition, idString } from "./db_utils";
import { SimpleGrid, useSimpleGrid } from "./components/simple_grid";
import { filterFieldItemsForUser } from "./services/field_selector_scope";

const useStyles = FC.makeStyles({
    root: {
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        overflowY: "auto",
        flexDirection: "column",
    },
    header: {
        minHeight: "48px",
        height: "48px",
        color: "white",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        backgroundColor: "#0078D4",
        ...FC.shorthands.padding("10px"),
        ...FC.shorthands.gap("16px"),
    },
    sidebar: {
        width: "160px",
        overflowY: "auto",
        height: "100%",
        backgroundColor: FC.tokens.colorBrandBackground2,
    },
});

const Sidebar = () => {
    let { svr } = useContext(Context);
    let nav = useNavigate();

    const goTo = (url) => {
        return () => {
            console.log("🚀 goTo called with URL:", url);
            console.log("🚀 Current URL before nav:", window.location.pathname);
            nav(url);
            setTimeout(() => {
                console.log("🚀 Current URL after nav:", window.location.pathname);
                console.log("🚀 Full location:", window.location.href);
            }, 100);
        };
    };

    const handleShutdown = () => {
        fetch(`${svr}/shutdown`, {
            method: "POST",
            mode: "no-cors",
            body: JSON.stringify({}),
        });
    };

    let styles = useStyles();
    let { user } = useContext(Context);
    console.log("user:", user);

    const canonicalizeGroup = (value) => {
        if (!value) {
            return null;
        }

        const lookup = {
            admin: "Admin",
            Admin: "Admin",
            QA: "QA",
            qa: "QA",
            Annotator: "Annotator",
            annotator: "Annotator",
            FieldOperator: "FieldOperator",
            fieldoperator: "FieldOperator",
            field_operator: "FieldOperator",
            "field-admin": "FieldOperator",
            "field_admin": "FieldOperator",
            BackendOperator: "BackendOperator",
            backendoperator: "BackendOperator",
            backend_operator: "BackendOperator",
            staff: "BackendOperator",
            operator: "BackendOperator",
        };

        return lookup[value] || lookup[value?.toLowerCase?.()] || null;
    };

    const resolvedGroups = (() => {
        if (Array.isArray(user?.groups) && user.groups.length) {
            const mapped = user.groups
                .map(canonicalizeGroup)
                .filter(Boolean);
            if (mapped.length) {
                return Array.from(new Set(mapped));
            }
        }

        const single = canonicalizeGroup(user?.role);
        return single ? [single] : [];
    })();

    const hasGroup = (group) => resolvedGroups.includes(group);
    const isAdmin = hasGroup("Admin");
    const isFieldOperator = hasGroup("FieldOperator");
    const isQA = hasGroup("QA");
    const isBackendOperator = hasGroup("BackendOperator");
    const isBackendOnly = resolvedGroups.length > 0 && resolvedGroups.every((group) => group === "BackendOperator");

    return (
        <div className={styles.sidebar}>
            <FCU.Tree
                aria-label="Tree"
                defaultOpenItems={["data", "task", "field", "equipment", "system"]}
            >
                <FCU.TreeItem itemType="branch" value="data">
                    <FCU.TreeItemLayout>
                        <h3>数据中心</h3>
                    </FCU.TreeItemLayout>
                    <FCU.Tree defaultCheckedItems={["data"]}>
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout
                                onClick={() => {
                                    console.log("🔥 Clicking 风场数据, navigating to /datacenter/field");
                                    goTo("/datacenter/field")();
                                }}
                            >
                                风场数据
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout
                                onClick={goTo("/datacenter/task")}
                            >
                                巡检数据
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout
                                onClick={goTo("/datacenter/defection")}
                            >
                                缺陷数据
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                        {isAdmin && (
                            <FCU.TreeItem itemType="leaf">
                                <FCU.TreeItemLayout>
                                    <FC.Link
                                        href="/dashboard"
                                        target="_blank"
                                        appearance="subtle"
                                    >
                                        数据大屏
                                    </FC.Link>
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                        )}
                    </FCU.Tree>
                </FCU.TreeItem>
                <FCU.TreeItem itemType="branch" value="task">
                    <FCU.TreeItemLayout>
                        <h3>巡检任务</h3>
                    </FCU.TreeItemLayout>
                    <FCU.Tree>
                        {!isBackendOnly && (
                            <FCU.TreeItem itemType="leaf">
                                <FCU.TreeItemLayout
                                    onClick={goTo("/task/management")}
                                >
                                    任务管理
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                        )}
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout onClick={goTo("/task/check")}>
                                巡检工作
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout onClick={goTo("/task/annotation")}>
                                后台处理
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout onClick={goTo("/task/recheck")}>
                                缺陷复核
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout onClick={goTo("/task/report")}>
                                报告管理
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                    </FCU.Tree>
                </FCU.TreeItem>
                {(isAdmin || isFieldOperator) && (
                    <FCU.TreeItem itemType="branch" value="field">
                        <FCU.TreeItemLayout>
                            <h3>风场管理</h3>
                        </FCU.TreeItemLayout>
                        <FCU.Tree>
                            {isAdmin && (
                                <FCU.TreeItem
                                    itemType="leaf"
                                    value="/field/field"
                                >
                                    <FCU.TreeItemLayout
                                        onClick={goTo("/field/field")}
                                    >
                                        风场配置
                                    </FCU.TreeItemLayout>
                                </FCU.TreeItem>
                            )}
                            <FCU.TreeItem itemType="leaf" value="turbine">
                                <FCU.TreeItemLayout
                                    onClick={goTo("/field/turbine")}
                                >
                                    风机配置
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                            <FCU.TreeItem itemType="leaf" value="leaf">
                                <FCU.TreeItemLayout
                                    onClick={goTo("/field/leaf")}
                                >
                                    叶片配置
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                            <FCU.TreeItem itemType="leaf" value="business">
                                <FCU.TreeItemLayout
                                    onClick={goTo("/field/business")}
                                >
                                    业务配置
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                        </FCU.Tree>
                    </FCU.TreeItem>
                )}
                {(isAdmin || isFieldOperator || isQA) && (
                    <FCU.TreeItem itemType="branch" value="equipment">
                        <FCU.TreeItemLayout>
                            <h3>器材管理</h3>
                        </FCU.TreeItemLayout>
                        {/* QA only gets the read-only entry points; the backend policies remain the boundary. */}
                        <FCU.Tree>
                            <FCU.TreeItem itemType="leaf" value="equipment-ledger">
                                <FCU.TreeItemLayout onClick={goTo("/equipment")}>
                                    器材台账
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                            <FCU.TreeItem itemType="leaf" value="equipment-service-records">
                                <FCU.TreeItemLayout onClick={goTo("/equipment/service-records")}>
                                    维保记录
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                        </FCU.Tree>
                    </FCU.TreeItem>
                )}
                <FCU.TreeItem itemType="branch" value="system">
                    <FCU.TreeItemLayout>
                        <h3>系统配置</h3>
                    </FCU.TreeItemLayout>
                    <FCU.Tree>
                        {/* <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout> 系统参数 </FCU.TreeItemLayout>
                        </FCU.TreeItem> */}
                        {/* <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout onClick={goTo("/system/log")}>
                                系统日志
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem> */}
                        {isAdmin && (
                            <>
                                <FCU.TreeItem itemType="leaf">
                                    <FCU.TreeItemLayout
                                        onClick={goTo("/system/rawdata")}
                                    >
                                        原始数据
                                    </FCU.TreeItemLayout>
                                </FCU.TreeItem>
                                <FCU.TreeItem itemType="leaf">
                                    <FCU.TreeItemLayout
                                        onClick={goTo("/system/account-index")}
                                    >
                                        账号检索
                                    </FCU.TreeItemLayout>
                                </FCU.TreeItem>
                                <FCU.TreeItem itemType="leaf">
                                    <FCU.TreeItemLayout
                                        onClick={goTo("/system/tenants")}
                                    >
                                        租户管理
                                    </FCU.TreeItemLayout>
                                </FCU.TreeItem>
                                <FCU.TreeItem itemType="leaf">
                                    <FCU.TreeItemLayout
                                        onClick={goTo("/system/asset-owners")}
                                    >
                                        业主管理
                                    </FCU.TreeItemLayout>
                                </FCU.TreeItem>
                                <FCU.TreeItem itemType="leaf">
                                    <FCU.TreeItemLayout
                                        onClick={goTo("/system/devices")}
                                    >
                                        设备管理
                                    </FCU.TreeItemLayout>
                                </FCU.TreeItem>
                                <FCU.TreeItem itemType="leaf">
                                    <FCU.TreeItemLayout onClick={goTo("/system/usage")}>
                                        用量报表
                                    </FCU.TreeItemLayout>
                                </FCU.TreeItem>
                            </>
                        )}
                        <FCU.TreeItem itemType="leaf">
                            <FCU.TreeItemLayout
                                onClick={goTo("/system/version")}
                            >
                                版本信息
                            </FCU.TreeItemLayout>
                        </FCU.TreeItem>
                        {isAdmin && (
                            <FCU.TreeItem itemType="leaf">
                                <FCU.TreeItemLayout>
                                    <FC.Dialog className="flex-1">
                                        <FC.DialogTrigger
                                            disableButtonEnhancement
                                        >
                                            <div className="flex-1">
                                                关闭服务器
                                            </div>
                                        </FC.DialogTrigger>
                                        <FC.DialogSurface>
                                            <FC.DialogBody>
                                                <FC.DialogTitle>
                                                    你确认关闭服务器吗？
                                                </FC.DialogTitle>
                                                <FC.DialogContent></FC.DialogContent>
                                                <FC.DialogActions>
                                                    <FC.DialogTrigger
                                                        disableButtonEnhancement
                                                    >
                                                        <FC.Button appearance="secondary">
                                                            不，我不想关闭服务器
                                                        </FC.Button>
                                                    </FC.DialogTrigger>
                                                    <FC.Button
                                                        appearance="primary"
                                                        onClick={handleShutdown}
                                                    >
                                                        是的，我要关闭服务器！
                                                    </FC.Button>
                                                </FC.DialogActions>
                                            </FC.DialogBody>
                                        </FC.DialogSurface>
                                    </FC.Dialog>
                                </FCU.TreeItemLayout>
                            </FCU.TreeItem>
                        )}
                    </FCU.Tree>
                </FCU.TreeItem>
            </FCU.Tree>
        </div>
    );
};

const listFieldItem = async (apiClient, filter) => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
        console.log("no currentUser when listFieldItem");
        return [];
    }
    try {
        // Get basic field items from API
        let fieldItems = await apiClient.getFieldItems();
        
        // Apply text filter if provided
        if (filter && filter.trim()) {
            const filterLower = filter.toLowerCase();
            fieldItems = fieldItems.filter(item => 
                field_item_searchable_columns.some(column => 
                    item[column]?.toString().toLowerCase().includes(filterLower)
                )
            );
        }
        
        // For now, add placeholder values for missing aggregations
        // TODO: Implement proper aggregation endpoints in backend
        fieldItems = fieldItems.map(item => ({
            ...item,
            turbine_count: 0, // Placeholder - needs backend aggregation
            task_count: 0,    // Placeholder - needs backend aggregation
            turbines: [],     // Placeholder - needs backend relationship
            recent_task: null // Placeholder - needs backend aggregation
        }));
        
        fieldItems = filterFieldItemsForUser(fieldItems, currentUser);
        
        return fieldItems;
    } catch (error) {
        console.error("Failed to load field items:", error);
        return [];
    }
};

const FieldList = ({ onSelect, currentField }) => {
    const { refresh, SearchInput, items, db } = useSimpleGrid({
        listItems: listFieldItem,
    });
    const [selectedItems, setSelectedItems] = useState([]);
    useEffect(() => {
        if (items && items.length > 0) {
            if (!currentField) {
                let item = items[0];
                setSelectedItems([item.id]);
                onSelect(items[0]);
            }
        } else {
            setSelectedItems([]);
        }
    }, [items]);
    const columns = [FieldItemDef.createTableColumn("name")];
    return (
        <div className="flex flex-col gap-2 text-white">
            <div className="bg-white flex flex-col gap-2 text-white">
                <FC.Label className="p-2">风场列表({items.length})</FC.Label>
                <SearchInput
                    style={{ flex: "none" }}
                    appearance="underline"
                    className="text-white"
                />
                {items && items.length > 0 && (
                    <SimpleGrid
                        items={items}
                        columns={columns}
                        noHeader
                        subtleSelection={true}
                        getRowId={FieldItemDef.getRowId}
                        selectionMode="single"
                        selectionAppearance="none"
                        selectedItems={selectedItems}
                        onSelectionChange={(evt, data) => {
                            let item = Array.from(data.selectedItems)[0];
                            item = items.find((it) => idString(it.id) === item);
                            setSelectedItems([item.id]);
                            onSelect(item);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const Header = ({ onSelectField, currentField }) => {
    let { user, setUser } = useContext(Context);
    let styles = useStyles();
    const [showFieldSelector, setShowFieldSelector] = useState(false);
    const appName =
        process.env.REACT_APP_APP_NAME || "风电叶片外部巡检数据平台";

    let nav = useNavigate();

    const doLogout = () => {
        logoutUser();
        // Also clear the user from Context
        if (setUser) {
            setUser(null);
        }
        nav("/login");
    };

    return (
        <div className={styles.header}>
            <div className="flex items-center gap-3">
                <img
                    src="/bowongtec-logo.png"
                    className="h-14 w-auto"
                    alt="上海伯望"
                />
                <span className="text-lg font-bold tracking-wide">{appName}</span>
            </div>
            <div style={{ flex: 1 }}></div>
            <FC.Popover withArrow>
                <FC.PopoverTrigger>
                    <div className="text-lg font-bold cursor-pointer hover:text-[#ff0] pr-8">
                        {/* 切吉石乃海风电场 */}
                        {currentField?.name}
                        &nbsp;
                        <IC.CaretDownFilled />
                    </div>
                </FC.PopoverTrigger>
                <FC.PopoverSurface>
                    {currentField && (
                        <FieldList
                            onSelect={onSelectField}
                            currentField={currentField}
                        />
                    )}
                </FC.PopoverSurface>
            </FC.Popover>
            {user && (
                <div className="flex flex-row justify-center items-center gap-2">
                    <IC.Person24Filled />
                    <span>{user.name}</span>
                    <FC.Menu>
                        <FC.MenuTrigger>
                            <FC.Button
                                icon={<IC.SettingsFilled color="#fff" />}
                                appearance="transparent"
                            />
                        </FC.MenuTrigger>
                        <FC.MenuPopover>
                            <FC.MenuList>
                                <FC.MenuItem onClick={doLogout}>
                                    退出登录
                                </FC.MenuItem>
                            </FC.MenuList>
                        </FC.MenuPopover>
                    </FC.Menu>
                </div>
            )}
        </div>
    );
};

const breadKeys = {
    task: {
        name: "巡检任务",
        management: {
            name: "任务管理",
        },
        check: {
            name: "巡检工作",
        },
        annotation: {
            name: "标注工作",
        },
        recheck: {
            name: "缺陷复核",
        },
        report: {
            name: "报告管理",
        },
    },
    field: {
        name: "风场管理",
        field: {
            name: "风场配置",
        },
        turbine: {
            name: "风机配置",
        },
        leaf: {
            name: "叶片配置",
        },
        user: {
            name: "用户配置",
        },
        business: {
            name: "业务配置",
        },
    },
    system: {
        name: "系统配置",
        rawdata: {
            name: "原始数据",
        },
        devices: {
            name: "设备管理",
        },
        usage: {
            name: "用量报表",
        },
        tenants: {
            name: "租户管理",
            manage: {
                name: "租户代管工作台",
            },
        },
        "asset-owners": {
            name: "业主管理",
        },
        "update-keys": {
            name: "全局 Update Keys",
        },
        version: {
            name: "版本信息",
        },
    },
    equipment: {
        name: "器材管理",
        "service-records": {
            name: "维保记录",
        },
    },
    datacenter: {
        name: "数据中心",
        field: {
            name: "风场数据",
        },
        task: {
            name: "任务数据",
        },
        viewtask: {
            name: "任务数据",
        },
        defection: {
            name: "缺陷数据",
        },
    },
};

const getNames = (segments) => {
    // segments = [...segments, "name"];
    let names = [];
    for (let i = 0; i < segments.length; i++) {
        let query = [...segments.slice(0, i + 1), "name"];
        let name = _.get(breadKeys, query, "详情");
        names.push(name);
    }
    return names;
};

const BreadCrumb = () => {
    let segments = useLocation().pathname.slice(1).split("/");
    let names = getNames(segments);
    return (
        <FCB.Breadcrumb>
            {segments.map((seg, key) => {
                return (
                    <Fragment key={key}>
                        <FCB.BreadcrumbItem>{names[key]}</FCB.BreadcrumbItem>
                        {key != segments.length - 1 && (
                            <FCB.BreadcrumbDivider />
                        )}
                    </Fragment>
                );
            })}
        </FCB.Breadcrumb>
    );
};

const Bench = () => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
            }}
        >
            {console.log("🔍 Rendering Routes, current pathname:", window.location.pathname)}
            <Routes>
                <Route path="" element={<Version />} />
                <Route
                    path="/datacenter/field"
                    element={
                        <>
                            {console.log("🎪 Route /datacenter/field matched!")}
                            <DatacenterFieldManagement />
                        </>
                    }
                />
                <Route
                    path="datacenter/task"
                    element={<DatacenterTaskManagement />}
                />
                <Route
                    path="datacenter/viewtask/:taskId"
                    element={<DatacenterViewTaskManagement />}
                />
                <Route
                    path="datacenter/viewframes"
                    element={<DatacenterViewFrames />}
                />
                <Route
                    path="datacenter/defection"
                    element={<DatacenterDefectionManagement />}
                />

                <Route path="system/version" element={<Version />} />
                <Route path="system/rawdata" element={<RawDataManagement />} />
                {/* Account management is tenant-scoped. These two URLs used to open cross-tenant
                    write pages, which is what made "which tenant am I writing into?" a property of
                    a form field. They stay reachable as bookmarks and send the operator to pick a
                    tenant first; they never render a writable page again. */}
                <Route path="system/customers" element={<TenantScopedEntryRedirect />} />
                <Route path="system/account-index" element={<AccountIndex />} />
                <Route path="system/tenants" element={<TenantManagement />} />
                <Route path="system/tenants/:tenantId/manage" element={<TenantManagementWorkbench />} />
                <Route path="system/asset-owners" element={<AssetOwnerManagement />} />
                <Route path="system/devices" element={<DeviceManagement />} />
                <Route path="system/usage" element={<UsageReport />} />
                <Route path="system/update-keys" element={<UpdateSigningKeyManagement />} />
                {/* <Route path="system/log" element={<Log />} /> */}
                <Route path="field/field" element={<FieldManagement />} />
                <Route path="field/turbine" element={<TurbineManagement />} />
                <Route path="field/leaf" element={<LeafManagement />} />
                <Route path="field/user" element={<TenantScopedEntryRedirect />} />
                <Route
                    path="field/business"
                    element={<BusinessManagement />}
                />
                <Route path="task/management" element={<TaskManagement />} />
                <Route path="task/new" element={<TaskDetail />} />
                <Route path="task/check" element={<TaskCheck />} />
                <Route path="task/annotation" element={<TaskAnnotation />} />
                <Route path="groundbox-gallery" element={<GroundboxGallery />} />
                <Route path="task/:taskId" element={<TaskDetail />} />
                <Route path="task/recheck" element={<TaskRecheck />} />
                <Route
                    path="task/recheck/:rawDataId"
                    element={<TaskRecheckDetail />}
                />
                <Route path="task/report" element={<TaskReport />} />
                {/* 无读权限的角色直接输入地址时给出拒绝页，而不是挂载页面后再收一串 403。 */}
                <Route
                    path="equipment"
                    element={
                        <EquipmentRouteGuard>
                            <EquipmentManagement />
                        </EquipmentRouteGuard>
                    }
                />
                {/* Static route first: otherwise "service-records" would bind to :equipmentId. */}
                <Route
                    path="equipment/service-records"
                    element={
                        <EquipmentRouteGuard>
                            <ServiceRecordList />
                        </EquipmentRouteGuard>
                    }
                />
                <Route
                    path="equipment/:equipmentId"
                    element={
                        <EquipmentRouteGuard>
                            <EquipmentDetail />
                        </EquipmentRouteGuard>
                    }
                />
            </Routes>
        </div>
    );
};

export const getCurrentFieldFromStorage = () => {
    let currentField = localStorage.getItem("currentField");
    if (!currentField) {
        return null;
    }
    try {
        currentField = JSON.parse(currentField);
    } catch (e) {
        console.error("getCurrentField:", e);
        return null;
    }
    return currentField;
};

export const getCurrentFieldId = () => {
    let currentField = getCurrentFieldFromStorage();
    if (!currentField) {
        return null;
    }
    let id = currentField?.id;
    if (!id) {
        return null;
    }
    if (id.id) {
        id = id.id;
        return `field_item:${id}`;
    }
    return id;
};

export const getCurrentFieldIdFromNew = () => {
    const fieldId = localStorage.getItem('currentFieldId');
    if (!fieldId) {
        return null;
    }
    return `field_item:${fieldId}`;
};

export const getCurrentFieldFromNew = async () => {
    const fieldId = localStorage.getItem('currentFieldId');
    if (!fieldId) {
        return null;
    }
    try {
        // Import apiClient dynamically to avoid circular dependencies
        const { apiClient } = await import('./services/api');
        const field = await apiClient.getFieldItem(fieldId);
        return field;
    } catch (error) {
        console.error('Failed to get current field:', error);
        return null;
    }
};

export const loadCurrentField = async (apiClient) => {
    let currentField = getCurrentFieldFromStorage();
    let shouldReload = false;
    if (!currentField) {
        let fieldItems = await listFieldItem(apiClient);
        currentField = fieldItems[0];
        shouldReload = true;
    }
    if (currentField) {
        saveCurrentFieldToStorage(currentField);
        if (shouldReload) {
            window.location.reload();
            return;
        }
        let id = currentField.id;
        return currentField;
    }
};

const saveCurrentFieldToStorage = async (currentField) => {
    currentField = JSON.stringify(currentField);
    localStorage.setItem("currentField", currentField);
};

export const Workspace = () => {
    let location = useLocation();
    console.log("🏠 Workspace component rendering, location:", location.pathname);
    
    let { user, db, apiClient, oss, svr } = useContext(Context);
    let nav = useNavigate();
    const [showFieldSelector, setShowFieldSelector] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    const myloadCurrentField = async (apiClient) => {
        let currentField = await loadCurrentField(apiClient);
        if (currentField) {
            setCurrentField(currentField);
        }
    };
    useEffect(() => {
        if (!apiClient) {
            return;
        }
        myloadCurrentField(apiClient);
    }, [apiClient]);

    useEffect(() => {
        if (user === null) {
            const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");
            if (hasToken) {
                return;
            }
            console.log("🚨 User is null and token missing, redirecting to login");
            nav("/login");
        }
    }, [user, nav]);

    const handleToggleFieldSelector = (show) => {
        setShowFieldSelector(show);
    };

    const handleSelectField = (field) => {
        console.log("selectField:", field);
        setCurrentField(field);
        saveCurrentFieldToStorage(field);
        // Also save the field ID for App.js to load correctly
        localStorage.setItem('currentFieldId', field.id.toString());
        window.location.reload();
        // saveCurrentField(field, db);
    };

    let styles = useStyles();
    return (
        <div className={styles.root} style={{}}>
            <Header
                currentField={currentField}
                onSelectField={handleSelectField}
            />
            <div
                style={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "row",
                    overflowY: "auto",
                }}
            >
                <Sidebar />
                <div className="flex flex-1">
                    {console.log("🔶 currentField check:", !!currentField, currentField)}
                    <div
                        style={{
                            flex: 1,
                            display: "grid",
                            overflowY: "auto",
                            gridTemplateRows: "auto auto 1fr",
                        }}
                    >
                        <BreadCrumb />
                        <FC.Divider />
                        <Bench />
                    </div>
                </div>
            </div>
        </div>
    );
};
